import { useMutation } from '@apollo/client';
import styled from 'styled-components/macro';

import { TextInputLight } from 'components/site/layouts/DefaultPageLayout/TextInputLight';
import { S_WebButtonSmaller } from 'elements/button/WebButton';
import {
    LoginEmail,
    LoginEmailVariables
} from 'libs/graphql/mutation/access/__generated__/LoginEmail';
import { LOGIN_EMAIL_MUTATION } from 'libs/graphql/mutation/access/accessMutation';
import { EColors, EShadows } from 'libs/styles/styling';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { GlobalContext } from 'session/state/context';
import { setLoggedUser } from 'session/state/user/userActions';
import { Localization } from 'static/localization/localization';

interface Props {
    className?: string;
}

export const EmailLogin = (props: Props): JSX.Element => {
    const { dispatch } = useContext(GlobalContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const [processing, setProcessing] = useState(false);

    const [loginByEmail] = useMutation<LoginEmail, LoginEmailVariables>(LOGIN_EMAIL_MUTATION, {
        variables: {
            email,
            password
        },
        onCompleted: (result) => {
            dispatch(
                setLoggedUser(
                    result.loginUser.user,
                    result.loginUser.tokenType,
                    result.loginUser.accessToken
                )
            );
            userMessage(EToastType.SUCCESS, Localization.WELCOME);
            navigate('/admin');
            setProcessing(false);
        },
        onError: (err) => {
            const r = err.message;
            setProcessing(false);
            if (r === 'USER_NOT_FOUND') {
                userMessage(EToastType.ERROR, Localization.ERROR_USER_NOT_FOUND);
            } else if (r === 'USER_INVALID_PASSWORD') {
                userMessage(EToastType.ERROR, Localization.ERROR_WRONG_PASSWORD);
            } else if (r === 'USER_BANNED') {
                userMessage(EToastType.ERROR, Localization.ERROR_USER_BANNED);
            } else if (r === 'USER_EMAIL_NOT_VERIFIED') {
                userMessage(EToastType.ERROR, Localization.ERROR_EMAIL_NOT_VERIFIED);
            } else {
                userMessage(EToastType.ERROR, Localization.ERROR_LOGIN);
            }
        }
    });
    const canLoginByEmail = email.length > 0 && password.length > 0;

    return (
        <S_EmailLogin className={props.className}>
            <form
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        if (canLoginByEmail) {
                            loginByEmail();
                        }
                    }
                }}
            >
                <S_TextInputLight
                    value={email}
                    label={Localization.EMAIL_OR_USER}
                    onChange={(value) => {
                        setEmail(value);
                    }}
                />

                <S_PasswordContainer>
                    <TextInputLight
                        value={password}
                        label={Localization.PASSWORD}
                        onChange={(value) => {
                            setPassword(value);
                        }}
                        type="password"
                    />

                    <S_ForgottenPasswordLink to="/forgotten-password">
                        {Localization.FORGOT_PASSWORD}
                    </S_ForgottenPasswordLink>
                </S_PasswordContainer>
                <S_Center>
                    <S_WebButton
                        shadow={EShadows.LIGHT}
                        disabled={!canLoginByEmail}
                        type={'primary'}
                        text={Localization.LOGIN_ACTION}
                        loading={processing}
                        onClick={() => {
                            setProcessing(true);
                            loginByEmail();
                        }}
                    />
                </S_Center>
            </form>
        </S_EmailLogin>
    );
};

const S_EmailLogin = styled.div``;

const S_PasswordContainer = styled.div`
    position: relative;
    margin-bottom: 20px;
`;

const S_TextInputLight = styled(TextInputLight)`
    margin-bottom: 20px;
`;

const S_ForgottenPasswordLink = styled(Link)`
    margin-top: 10px;
    display: flex;
    width: 100%;
    justify-content: right;
    font-size: 12px;
    color: ${EColors.DARK};
    &:hover {
        text-decoration: underline;
    }
`;

const S_Center = styled.div`
    display: flex;
    justify-content: center;
`;

const S_WebButton = styled(S_WebButtonSmaller)`
    margin: 0 0 15px 0;
`;
